import React from "react";
import { ModalProps, Spinner } from "react-bootstrap";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";

import {
  BaseButton,
  ConfirmModal,
  Error,
  IManualPaymentRecord,
  IPackage,
  IUser,
  SelectInput,
  Switch,
  TextAreaInput,
  TextInput,
} from "../../../../libs";
import { adminService } from "../../../../services";
import { ManualPaymentHistory } from "./ManualPaymentHistory";
import * as Styled from "./UpdateManualPaymentModal.styled";

interface IProps extends ModalProps {
  user: IUser;
  onRecorded?: () => void;
}

const paymentMethods = [
  { value: "BANK_TRANSFER", label: "Bank transfer" },
  { value: "ACH_TRANSFER", label: "ACH transfer" },
  { value: "WIRE_TRANSFER", label: "Wire transfer" },
  { value: "CHECK", label: "Check" },
  { value: "CASH", label: "Cash" },
  { value: "MONEY_ORDER", label: "Money order" },
  { value: "EXTERNAL_CARD", label: "External card / POS" },
  { value: "OTHER", label: "Other" },
] as const;

const formatMoney = (amount: number, currency: string) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);

const paymentReferenceLabel = (paymentMethod: string) => {
  if (paymentMethod === "CHECK") return "Check number / reference *";
  if (["BANK_TRANSFER", "ACH_TRANSFER", "WIRE_TRANSFER"].includes(paymentMethod)) {
    return "Transaction reference *";
  }
  if (paymentMethod === "CASH") return "Receipt / internal reference *";
  return "Safe payment reference *";
};

const apiErrorMessage = (error: any) =>
  error?.response?.data?.error?.message ||
  error?.response?.data?.message ||
  error?.message ||
  "Unable to record the manual payment.";

export const UpdateManualPaymentModal: React.FC<IProps> = ({ user, onRecorded, ...props }) => {
  const [packages, setPackages] = React.useState<IPackage[]>([]);
  const [history, setHistory] = React.useState<IManualPaymentRecord[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [switchingToStripe, setSwitchingToStripe] = React.useState(false);
  const requestId = React.useRef(
    `manual_${Date.now()}_${
      globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)
    }`
  );
  const isExistingManual = user.subscriptionInfo?.billingMode === "MANUAL";
  const hasStripeSubscription = Boolean(user.subscriptionInfo?.subscriptionId && !isExistingManual);

  const formik = useFormik({
    initialValues: {
      packageId: user.subscriptionInfo?.packageId || "",
      agreementType: "FIXED_TERM" as "FIXED_TERM" | "MONTHLY_RATE",
      agreedAmount: "",
      amountPaid: "",
      currency: "USD",
      durationValue: "1",
      durationUnit: "MONTHS" as "DAYS" | "MONTHS" | "YEARS",
      paidAt: new Date(),
      coverageStart: new Date(),
      paymentMethod: "BANK_TRANSFER",
      paymentMethodDescription: "",
      paymentReference: "",
      notes: "",
      switchTiming: "AT_PERIOD_END" as "AT_PERIOD_END" | "IMMEDIATE",
      confirmed: false,
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.packageId) errors.packageId = "* Select a package";
      if (!(Number(values.agreedAmount) > 0)) {
        errors.agreedAmount = "* Enter the negotiated amount";
      }
      if (!(Number(values.amountPaid) > 0)) {
        errors.amountPaid = "* Enter the amount actually received";
      }
      if (!Number.isInteger(Number(values.durationValue)) || Number(values.durationValue) < 1) {
        errors.durationValue = "* Enter a whole-number duration";
      }
      if (!values.paymentReference.trim()) {
        errors.paymentReference = "* Enter a safe payment reference";
      }
      if (values.paymentMethod === "OTHER" && !values.paymentMethodDescription.trim()) {
        errors.paymentMethodDescription = "* Describe the payment method";
      }
      if (!values.confirmed) {
        errors.confirmed = "* Review and confirm the billing change";
      }
      return errors;
    },
    onSubmit: async (values, helpers) => {
      try {
        const payment = await adminService.updateManualPayment(user.userId, {
          requestId: requestId.current,
          packageId: values.packageId,
          agreementType: values.agreementType,
          agreedAmount: Number(values.agreedAmount),
          amountPaid: Number(values.amountPaid),
          currency: values.currency,
          durationValue: Number(values.durationValue),
          durationUnit: values.durationUnit,
          paidAt: values.paidAt.toISOString(),
          coverageStart: values.coverageStart.toISOString(),
          paymentMethod: values.paymentMethod as any,
          paymentMethodDescription: values.paymentMethodDescription || undefined,
          paymentReference: values.paymentReference,
          notes: values.notes || undefined,
          switchTiming: values.switchTiming,
        });
        setHistory((current) => [payment, ...current]);
        toast.success(
          `Recorded ${formatMoney(payment.amountPaid, payment.currency)} and created Stripe invoice ${payment.stripeInvoiceNumber || payment.stripeInvoiceId}.`
        );
        onRecorded?.();
        props.onHide?.();
      } catch (error) {
        toast.error(apiErrorMessage(error));
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  React.useEffect(() => {
    if (!props.show) return;
    setLoading(true);
    Promise.all([adminService.getAllPackages(), adminService.getManualPayments(user.userId)])
      .then(([packageItems, payments]) => {
        setPackages(packageItems);
        setHistory(payments);
      })
      .catch((error) => toast.error(apiErrorMessage(error)))
      .finally(() => setLoading(false));
  }, [props.show, user.userId]);

  const selectedPackage = packages.find(
    (packageItem) => packageItem.id === formik.values.packageId
  );
  const startsAtRenewal = hasStripeSubscription && formik.values.switchTiming === "AT_PERIOD_END";
  const startsImmediately = hasStripeSubscription && formik.values.switchTiming === "IMMEDIATE";
  const agreementLabel =
    formik.values.agreementType === "MONTHLY_RATE"
      ? `${formik.values.currency} ${formik.values.agreedAmount || "0"} per month`
      : `${formik.values.currency} ${formik.values.agreedAmount || "0"} total`;

  const showSwitchToStripeConfirmation = () => {
    if (!selectedPackage) {
      toast.error("Select the package that should renew through Stripe.");
      return;
    }

    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmModal
          title="Switch to Stripe billing?"
          description={[
            {
              text: `Stripe will immediately attempt the first $${Number(selectedPackage.monthlyPrice).toFixed(2)} monthly charge for ${selectedPackage.name} using the customer's saved payment method. Manual billing will end, with no automatic credit or refund for unused manual access.`,
            },
          ]}
          noText="Keep manual billing"
          yesText="Charge & switch to Stripe"
          onNo={onClose}
          onYes={async () => {
            onClose();
            setSwitchingToStripe(true);
            try {
              const result = await adminService.switchManualBillingToStripe(user.userId, {
                packageId: selectedPackage.id,
              });
              toast.success(
                `${result.packageName} is now using Stripe billing. The next renewal is ${new Date(result.nextBillingAt * 1000).toLocaleDateString()}.`
              );
              onRecorded?.();
              props.onHide?.();
            } catch (error) {
              toast.error(apiErrorMessage(error));
            } finally {
              setSwitchingToStripe(false);
            }
          }}
        />
      ),
      closeOnClickOutside: false,
      closeOnEscape: true,
    });
  };

  return (
    <Styled.GenerateModal
      {...props}
      size="xl"
      dialogClassName="manual-billing-modal"
      keyboard
      backdrop="static"
    >
      <Styled.ModalWrapper>
        <Styled.HeaderRow>
          <div>
            <Styled.ModalTitle>Manage manual billing</Styled.ModalTitle>
            <Styled.ModalSubtitle>
              {user.name} · {user.email}
            </Styled.ModalSubtitle>
          </div>
          <Styled.HeaderActions>
            <Styled.ModeBadge $manual={isExistingManual}>
              {isExistingManual ? "Manual billing" : "Stripe billing"}
            </Styled.ModeBadge>
            <Styled.CloseButton type="button" aria-label="Close" onClick={props.onHide}>
              &times;
            </Styled.CloseButton>
          </Styled.HeaderActions>
        </Styled.HeaderRow>

        {loading ? (
          <Styled.LoadingState>
            <Spinner animation="border" size="sm" /> Loading billing details...
          </Styled.LoadingState>
        ) : (
          <>
            <Styled.Section>
              <Styled.SectionTitle>Plan and agreement</Styled.SectionTitle>
              {formik.errors.packageId && <Error message={formik.errors.packageId} />}
              <SelectInput
                fieldValue={formik.values.packageId}
                fieldName="packageId"
                fieldLabel="Public or private package *"
                options={packages.map((packageItem) => ({
                  id: packageItem.id,
                  value: packageItem.id,
                  label: `${packageItem.name} · $${Number(packageItem.monthlyPrice).toFixed(2)}/month standard`,
                }))}
                placeholder="Select package"
                onValueChange={(value) => formik.setFieldValue("packageId", value)}
                hasError={Boolean(formik.errors.packageId)}
              />
              <Styled.FieldGrid>
                <SelectInput
                  fieldValue={formik.values.agreementType}
                  fieldName="agreementType"
                  fieldLabel="Pricing arrangement *"
                  placeholder="Select arrangement"
                  options={[
                    { id: "fixed", value: "FIXED_TERM", label: "Fixed total for period" },
                    { id: "monthly", value: "MONTHLY_RATE", label: "Negotiated monthly rate" },
                  ]}
                  onValueChange={(value) => formik.setFieldValue("agreementType", value)}
                />
                <SelectInput
                  fieldValue={formik.values.currency}
                  fieldName="currency"
                  fieldLabel="Currency *"
                  placeholder="Select currency"
                  options={["USD", "CAD", "EUR", "GBP"].map((currency) => ({
                    id: currency,
                    value: currency,
                    label: currency,
                  }))}
                  onValueChange={(value) => formik.setFieldValue("currency", value)}
                />
              </Styled.FieldGrid>
              <Styled.FieldGrid>
                <div>
                  {formik.errors.agreedAmount && <Error message={formik.errors.agreedAmount} />}
                  <TextInput
                    fieldValue={formik.values.agreedAmount}
                    fieldName="agreedAmount"
                    fieldLabel={
                      formik.values.agreementType === "MONTHLY_RATE"
                        ? "Agreed monthly rate *"
                        : "Agreed total amount *"
                    }
                    placeholder="30.00"
                    onChangeHandler={formik.handleChange}
                    hasError={Boolean(formik.errors.agreedAmount)}
                  />
                </div>
                <div>
                  {formik.errors.amountPaid && <Error message={formik.errors.amountPaid} />}
                  <TextInput
                    fieldValue={formik.values.amountPaid}
                    fieldName="amountPaid"
                    fieldLabel="Actual amount received *"
                    placeholder="30.00"
                    onChangeHandler={formik.handleChange}
                    hasError={Boolean(formik.errors.amountPaid)}
                  />
                </div>
              </Styled.FieldGrid>
              <Styled.FieldGrid>
                <div>
                  {formik.errors.durationValue && <Error message={formik.errors.durationValue} />}
                  <TextInput
                    fieldValue={formik.values.durationValue}
                    fieldName="durationValue"
                    fieldLabel="Access duration *"
                    placeholder="3"
                    onChangeHandler={formik.handleChange}
                    hasError={Boolean(formik.errors.durationValue)}
                  />
                </div>
                <SelectInput
                  fieldValue={formik.values.durationUnit}
                  fieldName="durationUnit"
                  fieldLabel="Duration unit *"
                  placeholder="Select duration unit"
                  options={[
                    { id: "days", value: "DAYS", label: "Days" },
                    { id: "months", value: "MONTHS", label: "Months" },
                    { id: "years", value: "YEARS", label: "Years" },
                  ]}
                  onValueChange={(value) => formik.setFieldValue("durationUnit", value)}
                />
              </Styled.FieldGrid>
            </Styled.Section>

            {isExistingManual && (
              <Styled.Section>
                <Styled.SectionTitle>Return to Stripe billing</Styled.SectionTitle>
                <Styled.Summary>
                  <strong>Automatic renewal</strong>
                  <span>
                    Select the package above, then switch this customer to its standard monthly
                    Stripe price. The saved default payment method will be charged immediately.
                  </span>
                  <span>
                    If no card is saved or Stripe declines the payment, the account will remain on
                    manual billing.
                  </span>
                </Styled.Summary>
                <Styled.ActionRow>
                  <BaseButton
                    onClick={showSwitchToStripeConfirmation}
                    disabled={switchingToStripe || !selectedPackage}
                  >
                    {switchingToStripe ? "Switching..." : "Switch to Stripe billing"}
                  </BaseButton>
                </Styled.ActionRow>
              </Styled.Section>
            )}

            <Styled.Section>
              <Styled.SectionTitle>Payment received</Styled.SectionTitle>
              <Styled.FieldGrid>
                <SelectInput
                  fieldValue={formik.values.paymentMethod}
                  fieldName="paymentMethod"
                  fieldLabel="Payment method *"
                  placeholder="Select payment method"
                  options={paymentMethods.map((method) => ({
                    id: method.value,
                    value: method.value,
                    label: method.label,
                  }))}
                  onValueChange={(value) => formik.setFieldValue("paymentMethod", value)}
                />
                <Styled.DatePickerWrapper>
                  <Styled.DatePickerLabel>Payment date *</Styled.DatePickerLabel>
                  <Styled.CustomDatePicker
                    selected={formik.values.paidAt}
                    onChange={(date: Date) => formik.setFieldValue("paidAt", date)}
                    dateFormat="MM/dd/yyyy"
                  />
                </Styled.DatePickerWrapper>
              </Styled.FieldGrid>
              {formik.errors.paymentReference && <Error message={formik.errors.paymentReference} />}
              <TextInput
                fieldValue={formik.values.paymentReference}
                fieldName="paymentReference"
                fieldLabel={paymentReferenceLabel(formik.values.paymentMethod)}
                placeholder="Reference only — never enter full bank or card details"
                onChangeHandler={formik.handleChange}
                hasError={Boolean(formik.errors.paymentReference)}
              />
              {formik.values.paymentMethod === "OTHER" && (
                <>
                  {formik.errors.paymentMethodDescription && (
                    <Error message={formik.errors.paymentMethodDescription} />
                  )}
                  <TextInput
                    fieldValue={formik.values.paymentMethodDescription}
                    fieldName="paymentMethodDescription"
                    fieldLabel="Payment method description *"
                    placeholder="Describe how payment was received"
                    onChangeHandler={formik.handleChange}
                    hasError={Boolean(formik.errors.paymentMethodDescription)}
                  />
                </>
              )}
              <TextAreaInput
                fieldValue={formik.values.notes}
                fieldName="notes"
                fieldLabel="Internal notes"
                placeholder="Agreement or reconciliation notes"
                onChangeHandler={formik.handleChange}
              />
            </Styled.Section>

            <Styled.Section>
              <Styled.SectionTitle>Access and Stripe transition</Styled.SectionTitle>
              {hasStripeSubscription && (
                <SelectInput
                  fieldValue={formik.values.switchTiming}
                  fieldName="switchTiming"
                  fieldLabel="Switch from Stripe *"
                  placeholder="Select switch timing"
                  options={[
                    {
                      id: "renewal",
                      value: "AT_PERIOD_END",
                      label: "At current Stripe renewal date (recommended)",
                    },
                    {
                      id: "now",
                      value: "IMMEDIATE",
                      label: "Immediately — no automatic refund or proration",
                    },
                  ]}
                  onValueChange={(value) => formik.setFieldValue("switchTiming", value)}
                />
              )}
              {!hasStripeSubscription && (
                <Styled.DatePickerWrapper>
                  <Styled.DatePickerLabel>Manual access starts *</Styled.DatePickerLabel>
                  <Styled.CustomDatePicker
                    selected={formik.values.coverageStart}
                    onChange={(date: Date) => formik.setFieldValue("coverageStart", date)}
                    dateFormat="MM/dd/yyyy"
                  />
                </Styled.DatePickerWrapper>
              )}
              <Styled.Summary>
                <strong>Review</strong>
                <span>
                  {selectedPackage?.name || "Selected package"} · {agreementLabel}
                </span>
                <span>
                  Record {formik.values.currency} {formik.values.amountPaid || "0"} received via{" "}
                  {
                    paymentMethods.find((method) => method.value === formik.values.paymentMethod)
                      ?.label
                  }
                  .
                </span>
                <span>
                  Access begins{" "}
                  {startsAtRenewal
                    ? "at the current Stripe renewal date"
                    : startsImmediately
                      ? "immediately"
                      : formik.values.coverageStart.toLocaleDateString()}{" "}
                  for {formik.values.durationValue} {formik.values.durationUnit.toLowerCase()},
                  followed by a 7-day grace period.
                </span>
                <span>
                  A paid out-of-band Stripe invoice will be created. No card will be requested or
                  charged.
                </span>
              </Styled.Summary>
              {formik.errors.confirmed && <Error message={formik.errors.confirmed} />}
              <Styled.ConfirmWrapper>
                <Styled.ConfirmText>
                  I confirm the payment was received and these access dates are correct.
                </Styled.ConfirmText>
                <Switch
                  checked={formik.values.confirmed}
                  onChange={(event) => formik.setFieldValue("confirmed", event.target.checked)}
                />
              </Styled.ConfirmWrapper>
              <Styled.ActionRow>
                <BaseButton variant="secondary" onClick={props.onHide}>
                  Cancel
                </BaseButton>
                <BaseButton onClick={formik.handleSubmit} disabled={formik.isSubmitting}>
                  {formik.isSubmitting ? "Recording..." : "Record payment & enable manual billing"}
                </BaseButton>
              </Styled.ActionRow>
            </Styled.Section>

            <ManualPaymentHistory payments={history} />
          </>
        )}
      </Styled.ModalWrapper>
    </Styled.GenerateModal>
  );
};
